import { createSlice } from "@reduxjs/toolkit";
import {
  login,
  signup,
  logout,
  updateUserProfile,
  checkAuthState,
  signInWithGoogle,
  completeGoogleSignUp,
  refreshUser,
  getUserSubscription,
  updateUserPassword,
  validateCurrentPassword,
  cancelGoogleSignUp,
  addSellerLead,
  getUserDefaultPaymentMethod,
  checkCompanyNameExists,
} from "./authActions";

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  isUserUpdateLoading: false,
  isUserFetchLoading: true,
  error: null,
  companyExists: false,
  isCheckLoading: false,
  isAuthDialogOpen: false,
  redirectPage: null,
  authDialogDefaultTab: "login",
  authSuccessCallback: null,
  requiresRoleSelection: false,
  googleSignInData: null,
  currentSubscription: null,
  isUserSubscriptionLoading: false,
  subscriptionError: null,
  userPartsCount: 0,
  defaultPaymentMethod: null,
  isUserDefaultPaymentMethodLoading: false,
  defaultPaymentMethodError: null,
  isAddingSellerLead: false,
  sellerLeadError: null,
  sellerLeads: [],
  showVerification: false,
  verificationEmail: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    removeExistingUser: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    resetLoading: (state) => {
      state.isLoading = false;
    },
    setIsAuthDialogOpen: (state, action) => {
      state.isAuthDialogOpen = action.payload;
      if (!action.payload) {
        state.authSuccessCallback = null;
        state.redirectPage = null;
        state.requiresRoleSelection = false;
        state.googleSignInData = null;
      }
    },
    setVerificationEmail: (state, action) => {
      state.verificationEmail = action.payload;
    },
    setShowVerification: (state, action) => {
      state.showVerification = action.payload;
    },
    setAuthDialogDefaultTab: (state, action) => {
      state.authDialogDefaultTab = action.payload;
    },
    setAuthSuccessCallback: (state, action) => {
      state.authSuccessCallback = action.payload;
    },
    openAuthDialog: (state, action) => {
      const {
        showLoginFirstOrOnSuccess,
        onSuccessfulAuth,
        redirectPage,
        defaultTabOverride,
      } = action.payload;

      let tabToSet = "login";
      let successCb = null;

      if (typeof showLoginFirstOrOnSuccess === "function") {
        successCb = showLoginFirstOrOnSuccess;
        tabToSet = onSuccessfulAuth || "login";
      } else if (typeof showLoginFirstOrOnSuccess === "boolean") {
        tabToSet = showLoginFirstOrOnSuccess ? "login" : "signup";
        if (typeof onSuccessfulAuth === "function")
          successCb = onSuccessfulAuth;
        if (defaultTabOverride) tabToSet = defaultTabOverride;
      } else if (defaultTabOverride) {
        tabToSet = defaultTabOverride;
      }

      state.authDialogDefaultTab = tabToSet;
      state.redirectPage = redirectPage || null;
      if (successCb) {
        state.authSuccessCallback = () => successCb;
      } else {
        state.authSuccessCallback = null;
      }
      state.isAuthDialogOpen = true;
      // Reset verification state when opening dialog
      state.showVerification = false;
      state.verificationEmail = null;
    },
    manualLogout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isAuthDialogOpen = false;
      state.authSuccessCallback = null;
      state.error = null;
      state.requiresRoleSelection = false;
      state.googleSignInData = null;
      state.showVerification = false;
      state.verificationEmail = null;
    },
    resetGoogleSignInState: (state) => {
      state.requiresRoleSelection = false;
      state.googleSignInData = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    clearPasswordStatus: (state) => {
      state.passwordValidationError = null;
      state.updateError = null;
      state.updateSuccess = false;
    },
    getTotalCount: (state, action) => {
      state.userPartsCount = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(checkAuthState.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuthState.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = !!action.payload.user;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(checkAuthState.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload;
      })
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkCompanyNameExists.pending, (state) => {
        state.isCheckLoading = true;
      })
      .addCase(checkCompanyNameExists.fulfilled, (state, action) => {
        state.isCheckLoading = false;
        state.companyExists = action.payload.exists;
      })
      .addCase(checkCompanyNameExists.rejected, (state, action) => {
        state.isCheckLoading = false;
      })
      .addCase(validateCurrentPassword.pending, (state) => {
        state.validatingPassword = true;
        state.passwordValidationError = null;
      })
      .addCase(validateCurrentPassword.fulfilled, (state) => {
        state.validatingPassword = false;
        state.passwordValidationError = null;
      })
      .addCase(validateCurrentPassword.rejected, (state, action) => {
        state.validatingPassword = false;
        state.passwordValidationError = action.payload;
      })

      // update password
      .addCase(updateUserPassword.pending, (state) => {
        state.updatingPassword = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateUserPassword.fulfilled, (state) => {
        state.updatingPassword = false;
        state.updateSuccess = true;
      })
      .addCase(updateUserPassword.rejected, (state, action) => {
        state.updatingPassword = false;
        state.updateError = action.payload;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.isAuthDialogOpen = false;
        state.error = null;

        if (state.authSuccessCallback) {
          state.authSuccessCallback();
          state.authSuccessCallback = null;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(signInWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        const { isExistingUser, requiresRoleSelection } = action.payload;

        if (isExistingUser) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
          state.isLoading = false;
          state.isAuthDialogOpen = false;
          state.error = null;
          state.requiresRoleSelection = false;
          state.googleSignInData = null;

          if (state.authSuccessCallback) {
            state.authSuccessCallback();
            state.authSuccessCallback = null;
          }
        } else if (requiresRoleSelection) {
          state.isLoading = false;
          state.requiresRoleSelection = true;
          state.googleSignInData = action.payload;
          state.isAuthenticated = false;
        }
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload !== "SIGN_IN_CANCELLED") {
          state.error = action.payload;
        } else {
          state.error = null;
        }
        state.requiresRoleSelection = false;
        state.googleSignInData = null;
      })
      .addCase(completeGoogleSignUp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelGoogleSignUp.fulfilled, (state) => {
        state.isLoading = false;
        state.requiresRoleSelection = false;
        state.googleSignInData = null;
        state.error = null;
      })

      .addCase(cancelGoogleSignUp.rejected, (state, action) => {
        state.isLoading = false;
        state.requiresRoleSelection = false;
        state.googleSignInData = null;
        state.error = action.payload;
      })
      .addCase(completeGoogleSignUp.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.isAuthDialogOpen = false;
        state.error = null;
        state.requiresRoleSelection = false;
        state.googleSignInData = null;
        if (state.authSuccessCallback) {
          state.authSuccessCallback(action.payload.user.role);
          state.authSuccessCallback = null;
        }
      })
      .addCase(completeGoogleSignUp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(signup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.showVerification = true;
        state.verificationEmail = action.payload.user.email;
        state.error = null;

        if (state.authSuccessCallback) {
          state.authSuccessCallback(action.payload.user.role);
          state.authSuccessCallback = null;
        }
      })
      .addCase(signup.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.currentSubscription = null;
        state.defaultPaymentMethod = null;
        state.isAuthenticated = false;
        state.isAuthDialogOpen = false;
        state.authSuccessCallback = null;
        state.error = null;
        state.subscriptionError = null;
        state.isLoading = false;
        state.requiresRoleSelection = false;
        state.googleSignInData = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.isUserUpdateLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isUserUpdateLoading = false;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isUserUpdateLoading = false;
        state.error = action.payload;
      })
      .addCase(addSellerLead.pending, (state) => {
        state.isAddingSellerLead = true;
        state.sellerLeadError = null;
      })
      .addCase(addSellerLead.fulfilled, (state, action) => {
        state.isAddingSellerLead = false;
        if (!state.sellerLeads) {
          state.sellerLeads = [];
        }
        if (action.payload) {
          state.sellerLeads.push(action.payload);
        }
      })
      .addCase(addSellerLead.rejected, (state, action) => {
        state.isAddingSellerLead = false;
        state.sellerLeadError = action.payload;
      })
      // Refresh User
      .addCase(refreshUser.pending, (state) => {
        state.isUserFetchLoading = true;
        state.error = null;
      })
      .addCase(refreshUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.isUserFetchLoading = false;
        state.error = null;
      })
      .addCase(refreshUser.rejected, (state, action) => {
        state.isUserFetchLoading = false;
        state.error = action.payload;
      })
      // Get User Subscription
      .addCase(getUserSubscription.pending, (state) => {
        state.isUserSubscriptionLoading = true;
        state.subscriptionError = null;
      })
      .addCase(getUserSubscription.fulfilled, (state, action) => {
        state.currentSubscription = action.payload || null;
        state.isUserSubscriptionLoading = false;
        state.subscriptionError = null;
      })
      .addCase(getUserSubscription.rejected, (state, action) => {
        state.isUserSubscriptionLoading = false;
        state.subscriptionError = action.payload;
      })
      // Get User Default Payment Method
      .addCase(getUserDefaultPaymentMethod.pending, (state) => {
        state.isUserDefaultPaymentMethodLoading = true;
        state.defaultPaymentMethodError = null;
      })
      .addCase(getUserDefaultPaymentMethod.fulfilled, (state, action) => {
        state.defaultPaymentMethod = action.payload || null;
        state.isUserDefaultPaymentMethodLoading = false;
        state.defaultPaymentMethodError = null;
      })
      .addCase(getUserDefaultPaymentMethod.rejected, (state, action) => {
        state.isUserDefaultPaymentMethodLoading = false;
        state.defaultPaymentMethodError = action.payload;
      });
  },
});

export const {
  clearError,
  removeExistingUser,
  setToken,
  setIsAuthDialogOpen,
  setAuthDialogDefaultTab,
  setAuthSuccessCallback,
  openAuthDialog,
  manualLogout,
  setUser,
  clearPasswordStatus,
  resetGoogleSignInState,
  getTotalCount,
  resetLoading,
  setVerificationEmail,
  setShowVerification,
} = authSlice.actions;

export default authSlice.reducer;
